import { lazy, Suspense, useEffect, useState } from "react";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import { AppRoutes } from "@/constants/routes";
import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { useAuth } from "@/hooks/useAuth";
import { FieldValues, useForm, useWatch } from "react-hook-form";

import { RequestTypes } from "../../../../constants/enums";
import { CounterDeliveryDetail } from "../schema/counter-delivery.schema";
import { CounterDeliveryAmountAndActions } from "@/components/common/section-components/requests/counter-delivery/CounterDeliveryAmountAndActions";
import {
  fetchCounterDeliveryHandler,
  handleExecuteCounterDeliveryHandler,
  handleRejectCounterDeliveryHandler,
  sendCounterDeliveryCommunicationHandler,
  sendCounterDeliveryNegotiationHandler,
  updateCounterDeliveryAmountHandler,
} from "../schema/handler-functions";
import { useRoles } from "@/hooks/useRoles";
import { CounterDeliveryDetailMainDataCard } from "@/components/common/section-components/card/CounterDeliveryMainDataCard";
import { NegotiationLastMessage } from "@/components/common/section-components/chat/NegotiationLastMessage";

export const CounterDeliveryDetailPage = () => {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const handleBack = () => {
    navigate(AppRoutes.GRAIN_DELIVERY_PAYMENTS);
  };

  const [counterDelivery, setCounterDelivery] =
    useState<CounterDeliveryDetail | null>();
  const fetchCounterDelivery = async (showLoad: boolean = true) => {
    await fetchCounterDeliveryHandler({
      fetchPermissions,
      id,
      navigate,
      setCounterDelivery,
      setLoadingCounterDelivery,
      showLoad,
    });
  };

  const [loadingCounterDelivery, setLoadingCounterDelivery] =
    useState<boolean>(false);

  useEffect(() => {
    if (isActive && id && user) fetchCounterDelivery();
  }, [isActive, id, user]);

  const [isEditingAmount, setIsEditingAmount] = useState<boolean>(false);
  const [loadingEditAmount, setLoadingEditAmount] = useState<boolean>(false);
  const [loadingSendNegotiation, setLoadingSendNegotiation] =
    useState<boolean>(false);
  const [loadingSendCommunication, setLoadingSendCommunication] =
    useState<boolean>(false);

  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [loadingRejectCounterDelivery, setLoadingRejectCounterDelivery] =
    useState<boolean>(false);
  const [loadingExecuteCounterDelivery, setLoadingExecuteCounterDelivery] =
    useState<boolean>(false);

  const handleExecuteCounterDelivery = async (event?: FieldValues) => {
    await handleExecuteCounterDeliveryHandler({
      navigate,
      setLoadingExecuteCounterDelivery,
      counterDelivery,
      fetchPermissions,
      watchedValues: event,
    });
  };

  const { open: openExecuteModal, setOpen: setOpenExecuteModal } =
    useDisclosure();

  const handleRejectCounterDelivery = async (event?: FieldValues) => {
    await handleRejectCounterDeliveryHandler({
      navigate,
      setLoadingRejectCounterDelivery,
      setOpenRejectModal,
      event,
      counterDelivery,
      fetchPermissions,
    });
  };

  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm();

  const watchedValues = useWatch({
    control,
  });

  const handleEditAmount = async () => {
    await updateCounterDeliveryAmountHandler({
      errors,
      setLoadingEditAmount,
      counterDelivery,
      fetchCounterDelivery,
      setIsEditingAmount,
      watchedValues,
      navigate,
      fetchPermissions,
    });
  };

  useEffect(() => {
    if (counterDelivery && !counterDelivery?.sinBase) {
      setValue("sell_order_amount", counterDelivery?.montoSolicitado);
    }
  }, [counterDelivery]);

  const { setOpen: setOpenCommunication, open: openCommunication } =
    useDisclosure();

  const { open: openChatViewModal, setOpen: setOpenChatViewModal } =
    useDisclosure();

  const { fetchPermissions } = useRoles();

  const handleSendNegotiation = async () => {
    await sendCounterDeliveryNegotiationHandler({
      navigate,
      setLoadingSendNegotiation,
      counterDelivery,
      fetchCounterDelivery,
      fetchPermissions,
    });
  };

  const handleSendCommunication = async (event?: FieldValues) => {
    // No se pasa setOpenCommunicationModal: el chat es multi-turno y debe
    // permanecer abierto después de enviar.
    return await sendCounterDeliveryCommunicationHandler({
      navigate,
      setLoadingSendCommunication,
      event,
      counterDelivery,
      fetchCounterDelivery,
      fetchPermissions,
    });
  };

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title={"Solicitud de venta de granos a entregar"}
        onBack={handleBack}
        backButton
        loading={loadingCounterDelivery}
      />

      {!loadingCounterDelivery && (
        <>
          <CounterDeliveryAmountAndActions
            clearErrors={clearErrors}
            counterDelivery={counterDelivery}
            setIsEditingAmount={setIsEditingAmount}
            control={control}
            errors={errors}
            handleEditAmount={handleEditAmount}
            loadingEditAmount={loadingEditAmount}
            isEditingAmount={isEditingAmount}
            setOpenRejectModal={setOpenRejectModal}
            loadingRejectCounterDelivery={loadingRejectCounterDelivery}
            loadingExecuteCounterDelivery={loadingExecuteCounterDelivery}
            handleExecuteCounterDelivery={() => setOpenExecuteModal(true)}
            setValue={setValue}
            loadingSendNegotiation={loadingSendNegotiation}
            loadingSendCommunication={loadingSendCommunication}
            setOpenInfoModal={setOpenCommunication}
            handleSendNegotiation={handleSendNegotiation}
          />

          <CounterDeliveryDetailMainDataCard
            counterDelivery={counterDelivery}
          />

          <NegotiationLastMessage
            title="Último mensaje - Comunicación"
            messages={counterDelivery?.mensajes || []}
            onOpenChat={() => setOpenChatViewModal(true)}
            testId="counter-delivery-open-chat"
          />
        </>
      )}

      {counterDelivery?.id && (
        <Suspense>
          <RejectRequestModalDynamic
            open={openRejectModal}
            setOpen={setOpenRejectModal}
            handleAccept={handleRejectCounterDelivery}
            loadingAccept={loadingRejectCounterDelivery}
            idTipoSolicitud={RequestTypes.ContraEntrega}
            entityName="venta de granos a entregar"
          />
          <NegotiationChatModalDynamic
            open={openCommunication}
            setOpen={setOpenCommunication}
            title="Chat - En comunicación"
            messages={counterDelivery?.mensajes || []}
            handleSend={handleSendCommunication}
            loadingSend={loadingSendCommunication}
            idTipoSolicitud={RequestTypes.ContraEntrega}
            mensajesDisponibles={counterDelivery?.mensajesDisponibles}
          />

          <NegotiationChatModalDynamic
            open={openChatViewModal}
            setOpen={setOpenChatViewModal}
            title="Chat - En comunicación"
            messages={counterDelivery?.mensajes || []}
            readOnly
          />
          <ExecuteGrainRequestModalDynamic
            open={openExecuteModal}
            setOpen={setOpenExecuteModal}
            handleAccept={handleExecuteCounterDelivery}
            loadingAccept={loadingExecuteCounterDelivery}
          />
        </Suspense>
      )}
    </Flex>
  );
};

const RejectRequestModalDynamic = lazy(() =>
  import("@/components/modal/RejectRequestModal").then((el) => ({
    default: el["RejectRequestModal"],
  })),
);

const NegotiationChatModalDynamic = lazy(() =>
  import("@/components/modal/NegotiationChatModal").then((el) => ({
    default: el["NegotiationChatModal"],
  })),
);

const ExecuteGrainRequestModalDynamic = lazy(() =>
  import("@/components/modal/ExecuteGrainRequestModal").then((el) => ({
    default: el["ExecuteGrainRequestModal"],
  })),
);
